import FoodAdditionalDrawerMode from "@components/offer/models/FoodAdditionalDrawerMode";
import Restaurant from "@components/restaurant/models/Restaurant";
import UserAddress from "@components/user-address/models/UserAddress";
import RootStore from "@stores/RootStore";
import _, { clone } from "lodash";
import { action, computed, makeObservable, observable, runInAction } from "mobx";
import AddressOption from "../models/edit/AddressOption";
import OrderFoodRequest from "../models/edit/OrderFoodRequest";
import OrderRequest from "../models/edit/OrderRequest";
import Order from "../models/list/Order";
import OrderService from "../services/OrderService";
import SideDishRequest from "../models/edit/SideDishRequest";
import Food from "@components/offer/models/Food";
import { AxiosError } from "axios";

export default class OrderEditViewStore {
    rootStore: RootStore;
    service: OrderService;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        this.service = new OrderService();

        makeObservable(this, {
            //#region UI
            isLoading: observable,
            setIsLoading: action,

            isOrderCartDrawerOpen: observable,
            setIsOrderCartDrawerOpen: action,
            //#endregion

            insertedOrder: observable,
            setInsertedOrder: action,
            insertOrderAsync: action,
            onOrderUpdate: action,
            clearState: action,
            subscribtionId: computed,
            createOrderFoodSideDishRequests: action,
            setOrderRestaurant: action,

            isSubmitEnabled: computed,
            isOrderEmpty: computed,
            isAddressValid: computed,
            isUserLoggedIn: computed,
            isRestaurantDeliveryActive: computed,
            isRestaurantDeliveryEnabled: computed,
            isMinOrderAmountForDeliveryConditionValid: computed,

            orderFoods: observable,
            setOrderFoods: action,
            handleOnOrderFoodAddOrEdit: action,
            onOrderFoodEditClick: action,
            onOrderFoodRemoveClick: action,

            //#region _____________ Order information _____________

            totalAmount: observable,
            setTotalAmount: action,
            updateTotalAmount: action,

            amount: observable,
            setAmount: action,
            updateAmount: action,

            deliveryFee: observable,
            setDeliveryFee: action,
            updateDeliveryFee: action,

            restaurant: observable,
            setRestaurant: action,

            addressOption: observable,
            setAddressOption: action,
            userAddress: observable,
            setUserAddress: action,

            //#endregion
        })
    }

    //#region UI

    isLoading: boolean = false;
    setIsLoading = (value: boolean) => {
        this.isLoading = value
    }

    isOrderCartDrawerOpen: boolean = false;
    setIsOrderCartDrawerOpen = (value: boolean) => {
        if (value === false && this.insertedOrder !== undefined) {
            this.setInsertedOrder(undefined);
        }
        this.isOrderCartDrawerOpen = value;
    }

    //#endregion

    insertedOrder: Order | undefined = undefined;
    setInsertedOrder = (value: Order | undefined) => {
        this.insertedOrder = value;
    }

    insertOrderAsync = async () => {
        if (this.addressOption === "user-address" && this.userAddress === undefined) {
            return;
        }

        if (this.restaurant === undefined) {
            return;
        }

        var user = this.rootStore.authorizationStore.user;
        if (user === undefined || user === null) {
            return;
        }

        var orderFoodRequests: OrderFoodRequest[] = [];
        var totalAmount = 0;
        for (var orderFood of this.orderFoods) {
            var sideDishRequests = this.createOrderFoodSideDishRequests(orderFood);

            var orderFoodRequest: OrderFoodRequest = {
                foodId: orderFood.foodId,
                food: new Food(),
                foodSize: orderFood.foodSize,
                foodAmount: orderFood.foodAmount,
                sideDishAmount: orderFood.sideDishAmount,
                totalAmount: orderFood.totalAmount,
                sideDish: sideDishRequests,
                quantity: orderFood.quantity,
                note: orderFood.note
            }

            totalAmount += orderFood.totalAmount;

            orderFoodRequests.push(orderFoodRequest);
        }

        var orderRequest: OrderRequest = {
            address: this.addressOption === "user-address" ? this.userAddress!.address : null,
            locationDescription: this.addressOption === "user-address" ? this.userAddress!.locationDescription : null,
            isComingForOrder: this.addressOption === "isComingForOrder",
            isServedInRestaurant: this.addressOption === "isServedInRestaurant",
            restaurantId: this.restaurant.id,
            userId: user.id,
            orderFoods: orderFoodRequests,
            amount: totalAmount,
            totalAmount: totalAmount,
            deliveryFee: null,
            gPSCoordinate: null,
            restaurantTableId: null
        }

        try {
            this.setIsLoading(true);

            await this.rootStore.userOrderHub.subscribeToOrderHub();
            var response = await this.service.postAsync(orderRequest);

            this.onAfterOrderInsert(response.data)
        } catch (error) {
            await this.rootStore.userOrderHub.stopConnection();
            if (error instanceof AxiosError) {
                if (error.response?.status === 400) {
                    if (error.response.data === "Restaurant is offline!") {
                        this.rootStore.toastStore.alert(
                            "error",
                            "Greška",
                            "Restoran je izvan mreže."
                        );
                        return;
                    }
                }
            }

            this.rootStore.toastStore.alert(
                "error",
                "Greška",
                "Dogodila se neočekivana greška. Molimo pokušaj ponovo."
            )
        }
        finally {
            this.setIsLoading(false);
        }
    }

    onAfterOrderInsert = (insertedOrder: Order) => {
        runInAction(() => {
            this.clearState();
            this.setInsertedOrder(insertedOrder);
        })
    }

    onOrderUpdate = (updatedOrder: Order) => {
        if (this.insertedOrder !== undefined && this.insertedOrder.id === updatedOrder.id) {
            this.insertedOrder.isConfirmed = updatedOrder.isConfirmed;

            this.rootStore.userOrderHub.stopConnection();
        }
    }

    clearState = () => {
        runInAction(() => {
            this.setInsertedOrder(undefined);
            this.setRestaurant(undefined);
            this.orderFoods = [];
            this.setAddressOption(undefined);
        })
    }

    get subscribtionId() {
        var user = this.rootStore.authorizationStore.user;
        if (user !== undefined && user !== null) {
            return user.id;
        }

        return null;
    }

    get isSubmitEnabled() {
        return (
            this.isUserLoggedIn === true
            && this.restaurant?.isOnline === true
            && this.isAddressValid === true
            && this.isOrderEmpty === false
            && this.isMinOrderAmountForDeliveryConditionValid === true
        );
    }

    get isUserLoggedIn() {
        return this.rootStore.authorizationStore.isUserLoggedIn;
    }

    get isRestaurantDeliveryActive() {
        if (this.restaurant === undefined) {
            return true;
        }

        return this.restaurant.isDeliveryActive;
    }

    get isRestaurantDeliveryEnabled() {
        if (this.restaurant === undefined) {
            return true;
        }

        return this.restaurant.hasDelivery;
    }

    orderFoods: OrderFoodRequest[] = [];
    setOrderFoods = (orderFoods: OrderFoodRequest[]) => {
        this.orderFoods = orderFoods;
    }
    get isOrderEmpty() {
        return this.orderFoods.length === 0;
    }

    handleOnOrderFoodAddOrEdit = (orderFood: OrderFoodRequest, foodAdditionalDrawerMode: FoodAdditionalDrawerMode) => {
        var editedOrderFood = _.cloneDeep(orderFood);

        editedOrderFood.sideDish = this.createOrderFoodSideDishRequests(editedOrderFood);
        editedOrderFood.foodSize = editedOrderFood.food.foodPrices.find(p => p.isSelected)!?.size;

        if (foodAdditionalDrawerMode === "insert") {
            if (this.restaurant === undefined) {
                runInAction(() => {
                    this.setOrderRestaurant(editedOrderFood.food.restaurantId);
                })
            }
            else {
                if (this.restaurant.id !== orderFood.food.restaurantId) {
                    if (window.confirm("Želite dodati ponudu iz drugog restorana? Sva jela iz košarice će se izbrisati.")) {
                        this.setOrderRestaurant(editedOrderFood.food.restaurantId);
                    }
                    else {
                        return;
                    }
                }
            }

            var sameFoodIndex = this.orderFoods.findIndex(o => this.isSameOrderFood(o, editedOrderFood));
            if (sameFoodIndex > -1) {
                var sameFood = this.orderFoods[sameFoodIndex];
                sameFood.quantity += editedOrderFood.quantity;
                sameFood.sideDishAmount += editedOrderFood.sideDishAmount;
                sameFood.foodAmount += editedOrderFood.foodAmount;
                sameFood.totalAmount += editedOrderFood.totalAmount;
            }
            else {
                this.orderFoods.push(editedOrderFood);
            }
        }
        else if (foodAdditionalDrawerMode === "edit") {
            var currentOrderFoodIndex = this.orderFoods.findIndex(o => this.isSameOrderFood(o, this.orderFoodBeforeEdit));

            var sameOrderFoodIndex = this.orderFoods.findIndex(o => this.isSameOrderFood(o, editedOrderFood));
            if (sameOrderFoodIndex > -1 && (sameOrderFoodIndex !== currentOrderFoodIndex)) {
                var sameOrderFood = this.orderFoods[sameOrderFoodIndex];
                sameOrderFood.quantity += editedOrderFood.quantity;
                sameOrderFood.sideDishAmount += editedOrderFood.sideDishAmount;
                sameOrderFood.foodAmount += editedOrderFood.foodAmount;
                sameOrderFood.totalAmount += editedOrderFood.totalAmount;

                this.orderFoods.splice(currentOrderFoodIndex, 1);
            }
            else {
                var orderFoodForEdit = this.orderFoods[currentOrderFoodIndex];

                if (orderFoodForEdit !== undefined) {
                    orderFoodForEdit.quantity = editedOrderFood.quantity;
                    orderFoodForEdit.sideDish = editedOrderFood.sideDish;
                    orderFoodForEdit.food.foodPrices = editedOrderFood.food.foodPrices;
                    orderFoodForEdit.food.sideDishes = editedOrderFood.food.sideDishes;
                    orderFoodForEdit.note = editedOrderFood.note;
                    orderFoodForEdit.sideDishAmount = editedOrderFood.sideDishAmount;
                    orderFoodForEdit.foodAmount = editedOrderFood.foodAmount;
                    orderFoodForEdit.totalAmount = editedOrderFood.totalAmount;
                    orderFoodForEdit.foodSize = editedOrderFood.foodSize;
                }
            }
        }

        runInAction(() => {
            this.updateTotalAmount();
            this.updateDeliveryFee();
        })
    }

    setOrderRestaurant = (restaurantId: string) => {
        runInAction(() => {
            this.clearState();

            var restaurant = this.rootStore.restaurantOfferStore.restaurants.find(r => r.id === restaurantId);
            this.setRestaurant(restaurant);
        })
    }

    createOrderFoodSideDishRequests = (orderFood: OrderFoodRequest) => {
        var sideDishRequests: SideDishRequest[] | null = [];
        var selectedSideDishes = orderFood.food.sideDishes?.filter(p => p.isSelected);
        if (!_.isNil(selectedSideDishes) && selectedSideDishes.length > 0) {
            for (var selectedSideDish of selectedSideDishes) {
                var sideDishRequest: SideDishRequest = {
                    id: selectedSideDish.id,
                    name: selectedSideDish.name,
                    description: selectedSideDish.description
                }

                sideDishRequests.push(sideDishRequest);
            }
        }
        else {
            sideDishRequests = null;
        }

        return sideDishRequests;
    }

    onOrderFoodRemoveClick = (orderFood: OrderFoodRequest) => {
        runInAction(() => {
            var orderFoodToRemove = this.orderFoods.findIndex(o => this.isSameOrderFood(o, orderFood));
            this.orderFoods.splice(orderFoodToRemove, 1);

            this.updateTotalAmount();
            this.updateDeliveryFee();
            if (this.isOrderEmpty) {
                this.clearState();
            }
        })
    }

    orderFoodBeforeEdit: OrderFoodRequest | undefined;
    setOrderFoodBeforeEdit = (orderFoodBeforeEdit: OrderFoodRequest | undefined) => {
        this.orderFoodBeforeEdit = orderFoodBeforeEdit;
    }

    onOrderFoodEditClick = (orderFood: OrderFoodRequest) => {
        runInAction(() => {
            this.setOrderFoodBeforeEdit(_.cloneDeep(orderFood))
            this.rootStore.restaurantOfferStore.openFoodAdditionalDrawer(orderFood, "edit");
        })
    }

    isSameOrderFood = (orderFoodA: OrderFoodRequest, orderFoodB: OrderFoodRequest | undefined) => {
        if (orderFoodB === undefined) {
            return false;
        }

        if (
            orderFoodA.foodId === orderFoodB.foodId
            && orderFoodA.foodSize === orderFoodB.foodSize
            && JSON.stringify(orderFoodA.sideDish) === JSON.stringify(orderFoodB.sideDish)
            && orderFoodA.note === orderFoodB.note
        ) {
            return true;
        }
        else {
            return false;
        }
    }

    //#region _____________ Order information _____________

    totalAmount: number = 0;
    setTotalAmount = (value: number) => {
        this.totalAmount = value;
    }
    updateTotalAmount = () => {
        runInAction(() => {
            this.updateAmount();

            var amount = this.amount;
            var deliveryFee = this.deliveryFee ?? 0;

            var totalAmount = amount + deliveryFee;
            this.setTotalAmount(totalAmount);
        })
    }

    amount: number = 0;
    setAmount = (value: number) => {
        this.amount = value;
    }
    updateAmount = () => {
        if (this.orderFoods.length === 0) {
            this.setAmount(0);
        }
        else {
            var amount = _.sumBy(this.orderFoods, (f) => f.totalAmount);
            this.setAmount(amount);
        }
    }

    deliveryFee: number | null = null;
    setDeliveryFee = (value: number | null) => {
        this.deliveryFee = value;
    }

    updateDeliveryFee = () => {

        if (this.restaurant === undefined) {
            return;
        }

        const { deliveryFee, minOrderAmountForDelivery } = this.restaurant;

        if (!_.isNil(deliveryFee) && !_.isNil(minOrderAmountForDelivery)) {
            if (this.amount < minOrderAmountForDelivery) {
                this.setDeliveryFee(deliveryFee);
            }
            else if (deliveryFee !== null) {
                this.setDeliveryFee(null);
            }
        }
    }

    restaurant: Restaurant | undefined;
    setRestaurant = (restaurant: Restaurant | undefined) => {
        this.restaurant = restaurant;
    }

    get isAddressValid() {
        if (this.addressOption === undefined) {
            return false;
        }

        if (this.addressOption === "user-address") {
            var isUserAddressValid = this.userAddress !== undefined && !_.isNil(this.userAddress.address) && this.userAddress.address !== "";

            if (isUserAddressValid === false) {
                return false;
            }
        }
        return true;
    }

    addressOption: AddressOption = undefined;
    setAddressOption = (addressOption: AddressOption) => {
        this.addressOption = addressOption;
    }

    userAddress: UserAddress | undefined;
    setUserAddress = (userAddress: UserAddress | undefined) => {
        this.userAddress = userAddress;
    }

    get isMinOrderAmountForDeliveryConditionValid() {
        if (this.addressOption !== "user-address") {
            return true;
        }
        else if (_.isNil(this.restaurant)) {
            return false;
        }
        else if (this.restaurant.minOrderAmountForDelivery === null) {
            return true;
        }
        else if (this.amount >= this.restaurant.minOrderAmountForDelivery) {
            return true;
        }

        return false;
    }
}

//#endregion
